/**
 * AutoScanBar — the global progress strip for a running Auto Scan album
 * sweep. Mounted once in App (next to MyPokemonImportBar, same slot and
 * language) so the sweep visibly keeps running wherever the trainer
 * navigates; the Upload menu can close the moment the sweep starts.
 */
import { X } from "lucide-react";

import { dismissAutoScan, useAutoScan } from "@/lib/autoScanRunner";
import { openAppSettings } from "@/lib/screenshotLibrary";

export function AutoScanBar() {
  const state = useAutoScan();
  if (state.phase === "idle") return null;

  const pct =
    state.total > 0 ? Math.min(100, Math.round((state.done / state.total) * 100)) : 0;
  const busy = state.phase === "scanning" || state.phase === "saving";

  let line: string;
  if (state.phase === "scanning") {
    line = `Auto Scan: ${state.done} / ${state.total} screenshots`;
  } else if (state.phase === "saving") {
    line = "Auto Scan: saving…";
  } else if (state.phase === "error") {
    line = state.error ?? "Auto Scan failed.";
  } else {
    const s = state.summary;
    const bits: string[] = [];
    if (s) {
      if (s.myPokemonSaved > 0) bits.push(`${s.myPokemonSaved} Pokémon saved`);
      if (s.dexStateApplied > 0) bits.push(`${s.dexStateApplied} dex entries updated`);
      if (bits.length === 0 && s.saved > 0) bits.push("already up to date");
    }
    if (state.held > 0) bits.push(`${state.held} need review (see Upload)`);
    if (state.skipped > 0) bits.push(`${state.skipped} skipped`);
    line =
      bits.length > 0 ? `Auto Scan: ${bits.join(", ")}` : "Auto Scan: no new GO screenshots.";
  }

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl overflow-hidden"
      style={{
        bottom: "calc(var(--nx-safe-bottom) + 84px)",
        background: "var(--surface-dark-9)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
      }}
      data-testid="auto-scan-bar"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <p
          // Progress lines are short and stay on one line; error/done copy
          // must never truncate away its own advice.
          className={`text-[12px] font-bold flex-1 min-w-0 ${busy ? "truncate" : "leading-snug"}`}
          style={{
            color: state.phase === "error" ? "hsl(0 70% 60%)" : "var(--text-ink-bright)",
          }}
          data-testid="auto-scan-bar-text"
        >
          {line}
        </p>
        {state.errorKind === "photos-denied" && (
          <button
            type="button"
            onClick={() => void openAppSettings()}
            data-testid="auto-scan-bar-settings"
            className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold"
            style={{
              background: "var(--accent-fill-glow-faint)",
              border: "1px solid var(--accent-fill-glow-subtle)",
              color: "var(--text-ink-bright)",
            }}
          >
            Open Settings
          </button>
        )}
        {!busy && (
          <button
            type="button"
            onClick={dismissAutoScan}
            aria-label="Dismiss Auto Scan status"
            data-testid="auto-scan-bar-dismiss"
            className="grid place-items-center rounded-full shrink-0"
            style={{ width: 28, height: 28, color: "var(--text-tertiary)" }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {busy && (
        <div style={{ height: 3, background: "var(--accent-fill-glow-subtle)" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "var(--accent-fill)",
              transition: "width 200ms linear",
            }}
            data-testid="auto-scan-bar-fill"
          />
        </div>
      )}
    </div>
  );
}
